import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  useSearchRipeningBarChartQuery,
  useSearchRipeningPieChartQuery,
  useSearchSortingPieChartQuery,
} from "./ripeingDashboardApi";
import useTranslator from "../../../hooks/useTranslator";

export const RipeningCharts = () => {
  const { _t } = useTranslator();

  const [pieSortingFilter, setPieSortingFilter] = useState("monthly");
  const [pieRipeningFilter, setPieRipeningFilter] = useState("monthly");
  const [barFilter, setBarFilter] = useState("monthly");

  const todayLabel = _t("today", "Today");
  const weeklyLabel = _t("weekly", "Weekly");
  const monthlyLabel = _t("monthly", "Monthly");

  const filterMap = {
    [todayLabel]: "hourly",
    [weeklyLabel]: "weekly",
    [monthlyLabel]: "monthly",
  };

  const { data: barChartData } = useSearchRipeningBarChartQuery({
    filter: barFilter,
  });
  const { data: pieRipeningData } = useSearchRipeningPieChartQuery({
    filter: pieRipeningFilter,
  });
  const { data: pieSortingData } = useSearchSortingPieChartQuery({
    filter: pieSortingFilter,
  });

  const totalCasesLabel = _t("total_cases", "Total Cases");
  const casesLabel = _t("cases", "Cases");

  const casesColdStorage = "Cases in Cold Storage";
  const casesReadyRipening = "Cases ready for Ripening";

  const options = {
    chart: {
      type: "pie",
    },
    title: { text: "" },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: { enabled: false },
        showInLegend: true,
      },
    },
    legend: {
      layout: "vertical",
      align: "right",
      verticalAlign: "bottom",
      itemMarginBottom: 5,
      itemStyle: {
        color: "#8C97A7",
        fontSize: "14px",
      },
    },
    series: [
      {
        name: casesLabel,
        data: pieRipeningData?.data?.data.map((series) => ({
          name: series.name,
          y: series.y,
          color:
            series.name === casesColdStorage
              ? "#46C389"
              : series.name === casesReadyRipening
              ? "#FFBC55"
              : "#FE6565",
        })),
      },
    ],
  };

  const sortingCasesEnter = "Cases Enter in  Sorting";
  const sortingCasesSelected = "Cases Selected";
  const sortingCasesRejected = "Cases Rejected";

  const totalCases =
    pieSortingData?.data?.data.find((item) => item.name === sortingCasesEnter)
      ?.y || 0;

  const handlePieRipeningFilter = (label) =>
    setPieRipeningFilter(filterMap[label]);
  const handleBarFilter = (label) => setBarFilter(filterMap[label]);
  const handlePieSortingFilter = (label) =>
    setPieSortingFilter(filterMap[label]);

  const optionsDonut = {
    chart: { type: "pie" },
    title: {
      text: `<p>${totalCasesLabel}</p><br />${totalCases}`,
      verticalAlign: "middle",
      align: "center",
      y: -30,
    },
    tooltip: {
      pointFormat: `{series.name}: <b>{point.y}</b>`,
    },
    plotOptions: {
      pie: {
        innerSize: "70%",
        dataLabels: { enabled: false },
        showInLegend: true,
      },
    },
    legend: {
      layout: "vertical",
      align: "center",
      verticalAlign: "bottom",
      itemMarginBottom: 5,
      itemStyle: {
        color: "#8C97A7",
        fontSize: "14px",
      },
    },
    series: [
      {
        name: casesLabel,
        data: pieSortingData?.data?.data.map((series) => ({
          name: _t(series.name, series.name),
          y: series.y,
          color:
            series.name === sortingCasesEnter
              ? "#FFBC55"
              : series.name === sortingCasesSelected
              ? "#FF8D64"
              : series.name === sortingCasesRejected
              ? "#FE6565"
              : "#46C389",
        })),
      },
    ],
  };

  const roomCapacity = _t("room_capacity", "Room Capacity");
  const totalCasesEntered = _t("total_cases_entered", "Total Cases Entered");
  const emptyRoomSpace = _t("empty_room_space", "Empty Space in Room");

  const optionsBar = {
    chart: { type: "column" },
    title: { text: null, align: "left" },
    xAxis: {
      categories: barChartData?.data?.categories,
      labels: {
        style: { fontSize: "12px", color: "#8C97A7" },
      },
    },
    yAxis: { title: { text: casesLabel } },
    legend: {
      align: "center",
      verticalAlign: "top",
      layout: "horizontal",
    },
    tooltip: { shared: true },
    plotOptions: {
      column: {
        grouping: true,
        borderWidth: 0,
        dataLabels: { enabled: false },
      },
    },
    series: [
      {
        name: roomCapacity,
        data: barChartData?.data?.data.map((room) => room.cases_capacity),
        color: "#EE9C1F",
      },
      {
        name: totalCasesEntered,
        data: barChartData?.data?.data.map((room) => room.cases_entered),
        color: "#0F9959",
      },
      {
        name: emptyRoomSpace,
        data: barChartData?.data?.data.map((room) => room.empty_space),
        color: "#FF2727",
      },
    ],
  };

  return (
    <Row className="mb-3">
      <Col lg={8}>
        <div className="cmn_card">
          <div className="overview_header d-flex gap-2 align-items-center">
            <h6 className="mb-0 flex-grow-1">
              {_t("room_data_overview", "Room Data Overview")}
            </h6>
            <ul className="chart_filter">
              {[todayLabel, weeklyLabel, monthlyLabel].map((item, index) => (
                <li
                  key={index}
                  onClick={() => handleBarFilter(item)}
                  className={filterMap[item] === barFilter ? "active" : ""}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-2 position-relative">
            <HighchartsReact highcharts={Highcharts} options={optionsBar} />
          </div>
        </div>
      </Col>
      <Col lg={4}>
        <div className="cmn_card">
          <div className="overview_header d-flex gap-2 align-items-center">
            <h6 className="mb-0 flex-grow-1">
              {_t("sorting_data", "Sorting Data")}
            </h6>
            <ul className="chart_filter">
              {[todayLabel, weeklyLabel, monthlyLabel].map((item, index) => (
                <li
                  key={index}
                  onClick={() => handlePieSortingFilter(item)}
                  className={
                    filterMap[item] === pieSortingFilter ? "active" : ""
                  }
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-2 position-relative">
            <HighchartsReact highcharts={Highcharts} options={optionsDonut} />
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default RipeningCharts;
